import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DevUsersPanel } from './DevUsersPanel';
import { useAuthStore } from './useAuthStore';
import './auth.css';

// ─── Typewriter ───────────────────────────────────────────────────────────────

interface Phrase {
  prefix: string;
  word: string;
  lang: string;
}

const PHRASES: Phrase[] = [
  { prefix: '', word: 'Territorio. Movilidad. Decisión.', lang: 'es' },
  { prefix: '', word: 'Redes. Datos. Inteligencia.', lang: 'es' },
  { prefix: '', word: 'Infraestructura. Evidencia. Acción.', lang: 'es' },
  { prefix: '', word: 'Ciudad. Sistema. Prioridad.', lang: 'es' },
  { prefix: '', word: 'Movilidad. Territorio. Gestión.', lang: 'es' },
  { prefix: '', word: 'Datos. Análisis. Decisión.', lang: 'es' },
  { prefix: '', word: 'Redes. Flujos. Territorio.', lang: 'es' },
  { prefix: '', word: 'Diagnóstico. Escenario. Acción.', lang: 'es' },
  { prefix: '', word: 'Ciudad. Evidencia. Futuro.', lang: 'es' },
];

function useTypewriter(phrases: Phrase[]): { displayedPrefix: string; displayedWord: string } {
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedPrefix, setDisplayedPrefix] = useState(phrases[0].prefix);
  const [displayedWord, setDisplayedWord] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const cur = phrases[currentPhraseIndex];
    const nextIdx = (currentPhraseIndex + 1) % phrases.length;
    const next = phrases[nextIdx];
    const langChange = cur.lang !== next.lang;
    const pause = 2000;
    const curLen = displayedPrefix.length + displayedWord.length;
    const fullLen = cur.prefix.length + cur.word.length;
    const delay = !isDeleting && curLen === fullLen ? pause : isDeleting ? 50 : 100;

    function tickDelete(): void {
      if (!langChange) {
        if (displayedWord.length > 0) {
          setDisplayedWord(p => p.slice(0, -1));
          return;
        }
        setIsDeleting(false);
        setCurrentPhraseIndex(nextIdx);
        setDisplayedPrefix(next.prefix);
        return;
      }
      if (displayedWord.length > 0) {
        setDisplayedWord(p => p.slice(0, -1));
        return;
      }
      if (displayedPrefix.length > 0) {
        setDisplayedPrefix(p => p.slice(0, -1));
        return;
      }
      setIsDeleting(false);
      setCurrentPhraseIndex(nextIdx);
    }

    function tickType(): void {
      const fullText = cur.prefix + cur.word;
      if (!langChange) {
        if (displayedWord.length < cur.word.length) {
          setDisplayedWord(cur.word.slice(0, displayedWord.length + 1));
          return;
        }
        setTimeout(() => setIsDeleting(true), pause);
        return;
      }
      if (curLen < fullText.length) {
        const ch = fullText[curLen];
        if (curLen < cur.prefix.length) {
          setDisplayedPrefix(p => p + ch);
          return;
        }
        setDisplayedWord(p => p + ch);
        return;
      }
      setTimeout(() => setIsDeleting(true), pause);
    }

    const timer = setTimeout(isDeleting ? tickDelete : tickType, delay);
    return () => clearTimeout(timer);
  }, [displayedPrefix, displayedWord, isDeleting, currentPhraseIndex, phrases]);

  return { displayedPrefix, displayedWord };
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function LoginBackground(): JSX.Element {
  return (
    <>
      <div className="auth-bg-image auth-bg-1" />
      <div className="auth-bg-image auth-bg-2" />
      <div className="auth-bg-image auth-bg-3" />
      <div className="auth-bg-image auth-bg-4" />
      <div className="auth-bg-image auth-bg-5" />
      <div className="auth-bg-image auth-bg-6" />
      <div className="auth-page-overlay" />
    </>
  );
}

function EyeIcon({ visible }: { visible: boolean }): JSX.Element {
  if (visible) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M12 5C7 5 2.73 8.11 1 12C2.73 15.89 7 19 12 19C17 19 21.27 15.89 23 12C21.27 8.11 17 5 12 5Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3 3L21 21M10.584 10.587C10.2087 10.962 9.99775 11.4708 9.99756 12.0013C9.99738 12.5319 10.208 13.0408 10.583 13.416C10.958 13.7913 11.4668 14.0022 11.9973 14.0024C12.5279 14.0026 13.0368 13.792 13.412 13.417M17.357 17.349C15.726 18.449 13.942 19 12 19C7 19 2.73 15.02 1 12C2.01 9.87 3.5 8.02 5.35 6.65L17.357 17.349ZM9.878 5.121C10.582 4.933 11.291 4.833 12 4.833C17 4.833 21.27 8.813 23 11.843C22.393 12.995 21.628 14.02 20.74 14.89L9.878 5.121Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface LoginFormProps {
  email: string;
  password: string;
  showPassword: boolean;
  isLoading: boolean;
  error: string | null;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onTogglePassword: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  t: (key: string) => string;
}

function LoginForm({
  email,
  password,
  showPassword,
  isLoading,
  error,
  onEmailChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
  t,
}: LoginFormProps): JSX.Element {
  return (
    <div className="auth-login-panel">
      <form onSubmit={onSubmit} className="auth-form-compact">
        <input
          id="email"
          type="email"
          value={email}
          required
          disabled={isLoading}
          placeholder={t('emailPlaceholder')}
          autoComplete="email"
          className="input-compact"
          onChange={e => onEmailChange(e.target.value)}
        />
        <div className="password-input-wrapper">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            required
            disabled={isLoading}
            placeholder={t('passwordPlaceholder')}
            autoComplete="current-password"
            className="input-compact"
            onChange={e => onPasswordChange(e.target.value)}
          />
          <button
            type="button"
            onClick={onTogglePassword}
            className="password-toggle-btn"
            disabled={isLoading}
            aria-label={showPassword ? t('hidePassword') : t('showPassword')}
          >
            <EyeIcon visible={showPassword} />
          </button>
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className={`btn-compact ${isLoading ? 'loading' : ''}`}
          aria-busy={isLoading}
        >
          →
        </button>
      </form>
      {error && (
        <div className="auth-error-below" role="alert" aria-live="polite">
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

/**
 * Página de Login
 *
 * @remarks
 * Redirige a /dashboard tras login exitoso (todos los roles).
 */
/** Valida que el next param sea una ruta interna segura (empieza con /). */
function safeNext(next: string | null): string {
  if (!next) return '/dashboard';
  const decoded = decodeURIComponent(next);
  if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  return '/dashboard';
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error, clearError } = useAuthStore();
  const { displayedPrefix, displayedWord } = useTypewriter(PHRASES);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    clearError();
    void (async (): Promise<void> => {
      try {
        await login({ email, password });
        navigate(safeNext(searchParams.get('next')));
      } catch {
        // Error ya manejado por el store
      }
    })();
  };

  return (
    <div className="auth-page">
      <LoginBackground />
      <div className="auth-login-centered-group">
        <div className="login-brand" aria-label="Wayline">
          <img
            src="/images/wayline-isotipo.svg"
            alt=""
            aria-hidden="true"
            className="login-brand-mark"
          />
          <span className="login-brand-wordmark">WAYLINE</span>
        </div>
        <LoginForm
          email={email}
          password={password}
          showPassword={showPassword}
          isLoading={isLoading}
          error={error}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onTogglePassword={() => setShowPassword(v => !v)}
          onSubmit={handleSubmit}
          t={t}
        />
      </div>
      <div className="auth-center-content">
        <div className="auth-brand-text">
          <span className="auth-brand-fixed">{displayedPrefix}</span>
          <span className="auth-brand-dynamic">{displayedWord}</span>
          <span className="auth-brand-cursor">|</span>
        </div>
      </div>
      <div className="auth-footer-copyright">
        <p>{t('copyright')}</p>
      </div>
      <DevUsersPanel />
    </div>
  );
}
